import React, { useEffect, useState } from "react";
import { useInfiniteQuery, useQueryClient } from "react-query";
import { useOpticsStore } from "../../../stores/use-optics-store";
import { PaginationTypes } from "@Shared/Pagination-Types";
import PagesPreivew from "../preview/PagesPreview";
import { mapPage } from "../usePages";
import CustomEmptyState from "../../../components/empty-state/CustomEmptyState";
import { CUSTOM_EMPTY_STATE_KEY } from "../../../components/empty-state/custom-templates";
import { searchPages } from "../../../api/page-api";
import { usePagesStore } from "../../../stores/use-page-store";
import { useAtlassianLocale } from "@Shared/Hooks/useAtlassianLocale";

function DynamicPages({ mode }) {
  const [page, setPage] = useState(0);
  const [atlassianContext, setAtlassianContext] = useState();

  const { config, selectedTab } = usePagesStore((state) => ({
    config: state.data.config,
    selectedTab: state.getSelectedTab(),
  }));

  const [locale] = useAtlassianLocale();

  const isCosmosInstalled = useOpticsStore((state) => state.isCosmosInstalled);

  const queryClient = useQueryClient();

  useEffect(() => {
    async function fetchData() {
      const context = await window.AP.context.getContext();
      setAtlassianContext(context);
    }
    fetchData();
  }, []);

  const { tabs, variant, pagination, size } = config || {};
  const { limit } = tabs[selectedTab] || {};

  const { isFetching, fetchNextPage, hasNextPage, data } = useInfiniteQuery(
    [
      "pages",
      {
        labels: config.tabs[selectedTab].labels,
        contributors: config.tabs[selectedTab].contributors,
        spaces: config.tabs[selectedTab].spaces,
        parentPageId: config.tabs[selectedTab].parentPage,
        limit: config.tabs[selectedTab].limit,
        sorting: config.tabs[selectedTab].sorting,
        ordering: config.tabs[selectedTab].ordering,
        excludePersonalSpaces:
          config.tabs[selectedTab]?.excludePersonalSpaces || false,
        currentSpaceKey: atlassianContext?.confluence?.space?.key,
        currentPageId: atlassianContext?.confluence?.content?.id,
      },
    ],
    searchPages,
    {
      retry: 0,
      select: (response) => {
        if (!response?.pages?.length) return undefined;

        const { pages } = response;

        const builtData = pages.reduce((acc, page, index) => {
          const body = JSON.parse(page.body);
          const blogs =
            body?.results?.map((page) =>
              mapPage(page, locale, isCosmosInstalled)
            ) || [];

          return {
            ...acc,
            [index]: blogs,
          };
        }, {});

        return builtData;
      },
      getNextPageParam: (lastPage) => {
        const body = JSON.parse(lastPage.body);

        if (body._links.next) {
          const urlParams = new URLSearchParams(encodeURI(body._links.next));
          const cursor = urlParams.get("cursor");
          return cursor;
        }
        return false;
      },
    }
  );

  useEffect(() => {
    queryClient.resetQueries({ queryKey: "page" });
    setPage(0);
  }, [queryClient, pagination, variant, size, limit, selectedTab]);

  const queryPagination = {
    disabled: config.pagination === PaginationTypes.NONE,
    hasNextPage: hasNextPage || Object.keys(data || {})?.length - 1 > page,
    hasPrevPage: page >= 1,
    onNext: () => {
      if (isFetching) return;
      if (hasNextPage && Object.keys(data || {})?.length - 1 === page) {
        fetchNextPage();
      }
      if (hasNextPage || Object.keys(data || {})?.length - 1 > page) {
        setPage(page + 1);
      }
    },
    onPrev: () => {
      if (page >= 1) {
        setPage(page - 1);
      }
    },
    page,
    setPage,
  };

  if (!Object.values(data || {})?.[0]?.length) {
    return (
      <CustomEmptyState
        templateKey={CUSTOM_EMPTY_STATE_KEY.NEWS_EMPTY_RESULTS}
      />
    );
  }

  return (
    <PagesPreivew data={data} queryPagination={queryPagination} mode={mode} />
  );
}

export default DynamicPages;
